/**
 * Central design tokens (D1). Single source for Vuetify theme colours and
 * mirrored CSS custom properties in tokens.css.
 */

/** Brand primary — icons, borders, decorative accents (not button fill). */
export const brandPrimary = '#0887dc';
export const brandPrimaryDark = '#38a9ee';

/** Primary button / filled control background. */
export const primaryFillLight = '#0770b8';
export const primaryFillDark = '#0a78c4';

/** Soft primary wash for selected chips and cards (tokens.css --sw-primary-soft). */
export const primarySoftLight = '#e6f3fc';
export const primarySoftDark = '#162e49';

export const brandSuccess = '#6eaa34';
export const brandAi = '#cd7ec2';

/** Labels on success / accent (green) filled buttons. */
export const onAccentLight = '#ffffff';
export const onAccentDark = '#ffffff';
export const successFill = '#4e8a1f';

export const radiusSm = 8;
export const radiusMd = 12;
export const radiusPill = 999;

export const fontUi = "'Public Sans', sans-serif";

/** Editor / CKEditor body — unchanged from pre–Public Sans chrome (inherited Roboto). */
export const fontEditorContent = "'Roboto', sans-serif";

export const surfacesLight = {
    bg: '#f3f4f6',
    surface: '#ffffff',
    surface2: '#f9fafb',
    block: '#ffffff',
};

export const surfacesDark = {
    bg: '#0b1220',
    surface: '#111a2e',
    surface2: '#1e293b',
    block: '#111a2e',
};

export const neutralsLight = {
    neutralStrong: '#334155',
    neutralBody: '#4a5368',
    neutralMuted: '#6b7385',
    neutralSubtle: '#64748b',
    neutralInk: '#444455',
    neutralBorder: '#e2e8f0',
    neutralBorderStrong: '#d5dce8',
    neutralSurfaceAlt: '#edf0f5',
    neutralSurfaceSoft: '#f4f6fb',
};

export const neutralsDark = {
    neutralStrong: '#e2e8f0',
    neutralBody: '#cfd6e4',
    neutralMuted: '#9b9b9b',
    neutralSubtle: '#b9b9b9',
    neutralInk: '#ffffff',
    neutralBorder: 'rgba(255, 255, 255, 0.12)',
    neutralBorderStrong: 'rgba(255, 255, 255, 0.18)',
    neutralSurfaceAlt: surfacesDark.surface2,
    neutralSurfaceSoft: surfacesDark.bg,
};

/** Three elevation levels (sync with tokens.css --sw-shadow-*). */
export const shadowsLight = {
    sm: '0 1px 2px rgba(15, 23, 42, 0.06)',
    md: '0 4px 12px rgba(15, 23, 42, 0.08)',
    lg: '0 12px 32px rgba(15, 23, 42, 0.12)',
};

export const shadowsDark = {
    sm: '0 1px 2px rgba(0, 0, 0, 0.35)',
    md: '0 4px 12px rgba(0, 0, 0, 0.45)',
    lg: '0 12px 32px rgba(0, 0, 0, 0.55)',
};

/** Article preview reader chrome (D5 will wire these in ArticlePreview.vue). */
export const readerTokensLight = {
    shell: '#f3f4f6',
    bar: '#ffffff',
    paper: '#ffffff',
    text: '#1f2937',
    heading: '#0f172a',
    muted: '#5b6472',
    link: '#0770b8',
    linkVisited: '#6d3fb0',
    tocNum: '#0770b8',
    toc: '#f9fafb',
    quoteText: '#374151',
    quoteBg: '#f3f6fa',
    quoteLine: '#0887dc',
    th: '#eef2f7',
    thText: '#0f172a',
    zebra: '#f9fafb',
    border: '#e2e8f0',
    codeBg: '#f1f5f9',
    codeText: '#1e293b',
    mark: 'rgba(110, 170, 52, 0.20)',
    markText: '#1f2937',
    eat: '#f3f8ee',
    seg: '#f3f4f6',
    segText: '#334155',
    segOn: '#0770b8',
    segOnText: '#ffffff',
};

export const readerTokensDark = {
    shell: '#0b1220',
    bar: '#111a2e',
    paper: '#111a2e',
    text: '#dde3ec',
    heading: '#f8fafc',
    muted: '#94a3b8',
    link: '#7cc4f3',
    linkVisited: '#c4a8f0',
    tocNum: '#7cc4f3',
    toc: '#0f1729',
    quoteText: '#cbd5e1',
    quoteBg: '#162033',
    quoteLine: '#38a9ee',
    th: '#1e293b',
    thText: '#f1f5f9',
    zebra: '#0f1729',
    border: '#334155',
    codeBg: '#0b1220',
    codeText: '#e2e8f0',
    mark: 'rgba(110, 170, 52, 0.28)',
    markText: '#e5e7eb',
    eat: '#16231f',
    seg: '#1e293b',
    segText: '#cbd5e1',
    segOn: '#0a78c4',
    segOnText: '#ffffff',
};

export const buttonRadiusPx = radiusSm;
export const chipRadiusPx = radiusPill;
